import { useEffect, useRef } from 'react';
import { createClient } from '@lingaai/supabase-client';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Subscribe to real-time updates for a specific node and its relationships.
 * Automatically invalidates React Query cache when changes occur.
 *
 * @param nodeId - The UUID of the node to watch for changes
 * @param onFieldsChanged - Optional callback when node props change, receives array of changed field keys
 *
 * @example
 * ```tsx
 * function NodeDetailPage() {
 *   const { id } = useParams();
 *   const { data: node } = useNodeDetails(id);
 *   const [updatedFields, setUpdatedFields] = useState<Set<string>>(new Set());
 *
 *   // Auto-refresh when node or related data changes
 *   useNodeUpdates(id, (changedFields) => {
 *     setUpdatedFields(new Set(changedFields));
 *     setTimeout(() => setUpdatedFields(new Set()), 2000);
 *   });
 *
 *   return <NodeDetails node={node} />;
 * }
 * ```
 */
export function useNodeUpdates(nodeId?: string, onFieldsChanged?: (changedFields: string[]) => void) {
  const queryClient = useQueryClient();
  const previousPropsRef = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!nodeId) return;

    // Initialize previousPropsRef with current node data
    const currentNode = queryClient.getQueryData(['node', nodeId]) as any;
    if (currentNode?.props && !previousPropsRef.current) {
      previousPropsRef.current = currentNode.props;
      console.log('[useNodeUpdates] Initialized previousProps:', currentNode.props);
    }

    const supabase = createClient();
    const channel = supabase
      .channel(`node-updates-${nodeId}`)
      // Watch for changes to the node itself
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'nodes',
          filter: `id=eq.${nodeId}`,
        },
        (payload) => {
          console.log('[useNodeUpdates] Node changed:', payload);

          // Detect which fields changed
          if (payload.eventType === 'UPDATE' && payload.new && onFieldsChanged) {
            const newProps = (payload.new as any).props as Record<string, unknown>;
            const oldProps = previousPropsRef.current;

            console.log('[useNodeUpdates] Comparing props:', { oldProps, newProps });

            if (oldProps) {
              const changedFields: string[] = [];

              // Check for changed or new fields
              for (const [key, value] of Object.entries(newProps)) {
                if (JSON.stringify(oldProps[key]) !== JSON.stringify(value)) {
                  changedFields.push(key);
                  console.log(`[useNodeUpdates] Field "${key}" changed:`, { old: oldProps[key], new: value });
                }
              }

              // Check for deleted fields
              for (const key of Object.keys(oldProps)) {
                if (!(key in newProps)) {
                  changedFields.push(key);
                  console.log(`[useNodeUpdates] Field "${key}" deleted`);
                }
              }

              if (changedFields.length > 0) {
                console.log('[useNodeUpdates] ✨ Triggering blink animation for fields:', changedFields);
                onFieldsChanged(changedFields);
              } else {
                console.log('[useNodeUpdates] No fields changed');
              }
            } else {
              console.log('[useNodeUpdates] No previous props to compare (first update)');
            }

            previousPropsRef.current = newProps;
          }

          // Invalidate all queries related to this node
          queryClient.invalidateQueries({ queryKey: ['node', nodeId] });
          queryClient.invalidateQueries({ queryKey: ['node-details', nodeId] });
        }
      )
      // Watch for new edges FROM this node (outgoing relationships)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'edges',
          filter: `src=eq.${nodeId}`,
        },
        (payload) => {
          console.log('[useNodeUpdates] Outgoing edge changed:', payload);
          queryClient.invalidateQueries({ queryKey: ['node-details', nodeId] });
          queryClient.invalidateQueries({ queryKey: ['node-edges', nodeId] });
          queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
          queryClient.invalidateQueries({ queryKey: ['threads', nodeId] });
        }
      )
      // Watch for new edges TO this node (incoming relationships)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'edges',
          filter: `dst=eq.${nodeId}`,
        },
        (payload) => {
          console.log('[useNodeUpdates] Incoming edge changed:', payload);
          queryClient.invalidateQueries({ queryKey: ['node-details', nodeId] });
          queryClient.invalidateQueries({ queryKey: ['node-edges', nodeId] });
          queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[useNodeUpdates] Subscribed to updates for node:', nodeId);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[useNodeUpdates] Channel error for node:', nodeId);
        } else if (status === 'TIMED_OUT') {
          console.error('[useNodeUpdates] Subscription timed out for node:', nodeId);
        }
      });

    return () => {
      console.log('[useNodeUpdates] Unsubscribing from node:', nodeId);
      supabase.removeChannel(channel);
    };
  }, [nodeId, queryClient]);
}
